import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  ComplementOriginTranslator,
  WorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import {
  WorkOrderComplement,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useDisclosure, useRouteMatchParam } from '@/hooks';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import WorkOrderFormProvider from '../WorkOrderFormProvider';
import LGPDTermModal from '../Complements/ComplementForm/LGPDTermModal';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { WorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import {
  useCompaniesByLoggedEmployee,
  useCompany,
  useCompanySettings,
} from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  Company,
  CompanySettingsResponse,
} from '@/core/domain/enterprise/Companies/Companies.types';
import { isAppPortocar } from '@/index';
import { WorkOrderComplementFormData } from '../Complements/ComplementForm';
import moment from 'moment';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import WorkOrderVerifyPolicy from '../WorkOrderVerifyPolicy';
import { QueryObserverResult } from 'react-query';
import { WorkOrderItemData } from '../Items/WorkOrderItemList';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { InvoiceSettingsData } from '@/core/domain/invoice/settings/Settings.types';

interface WorkoderDetailContextProps {
  workorder: {
    data?: WorkOrder;
    isLoading: boolean;
    isFetching: boolean;
    refetch: VoidFunction;
    dataUpdatedAt: number;
  };
  complement: {
    data?: WorkOrderComplement;
    isLoading: boolean;
    refetch: () => Promise<QueryObserverResult<WorkOrderComplement, unknown>>;
    dataUpdatedAt: number;
    complementIdentifier?: string;
  };
  company: {
    data?: Company;
    companyConfig?: WorkorderConfigurationCompany;
    companySettings?: CompanySettingsResponse;
    invoiceSettingsByCompany: {
      data?: InvoiceSettingsData;
      isLoading?: boolean;
    };
  };
  isAlerted: boolean;
  setIsAlert(value: boolean): void;
  setComplementId: (complementId: number) => void;
  formatFormComplementData: () => WorkOrderComplementFormData | undefined;
  isPortocarWithClosedOrCancelled: boolean;
  items: {
    selectedItemsRows: WorkOrderItemData[];
    setSelectedItemsRows: React.Dispatch<
      React.SetStateAction<WorkOrderItemData[]>
    >;
  };
  complementCompanyId?: number;
  setComplementCompanyId: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
}

type Props = PropsWithChildren<unknown>;

const WorkoderDetailContext = createContext({} as WorkoderDetailContextProps);

export const useWorkorderDetailContext = () =>
  useContext(WorkoderDetailContext);

const WorkorderDetailProvider: React.FC<Props> = ({ children }) => {
  const workorderIdParam = +useRouteMatchParam('workOrderId');
  const complementIdParam = +useRouteMatchParam('complementId');

  const [workorderId, setWorkorderId] = useState<number>(workorderIdParam);
  const [complementId, setComplementId] = useState<number>(complementIdParam);
  const [openLgpd, { close }] = useDisclosure(true);
  const [isAlerted, setIsAlert] = useState<boolean>(false);
  const [complementCompanyId, setComplementCompanyId] = useState<number>();
  const [selectedItemsRows, setSelectedItemsRows] = useState<
    WorkOrderItemData[]
  >([]);

  useEffect(() => {
    if (workorderIdParam) {
      setWorkorderId(workorderIdParam);
    }

    if (complementIdParam) {
      setComplementId(complementIdParam);
    }
  }, [workorderIdParam, complementIdParam]);

  const { data: workorderData, ...workorderParams } = useWorkOrder(workorderId);

  const { data: complementData, ...complementParams } =
    useWorkOrderComplement(complementId);

  const companyId = workorderData?.company.id;
  const customer = workorderData?.customer;

  const { data: companyData } = useCompany(companyId);
  const { data: companyConfig } =
    useWorkorderConfigurationCompanyQuery(companyId);
  const { data: companySettings } = useCompanySettings(companyId);

  const invoiceSettingsByCompany =
    useInvoiceSettingsByCompany(complementCompanyId);

  invoiceSettingsByCompany.isLoading;

  const workorder = {
    data: workorderData,
    isLoading: workorderParams.isLoading,
    isFetching: workorderParams.isFetching,
    refetch: workorderParams.refetch,
    dataUpdatedAt: workorderParams.dataUpdatedAt,
  };

  const complement = {
    data: complementData,
    isLoading: complementParams.isLoading,
    refetch: complementParams.refetch,
    dataUpdatedAt: complementParams.dataUpdatedAt,
    complementIdentifier: complementData?.complementIdentifier,
  };

  const company = {
    data: companyData,
    companyConfig,
    companySettings,
    invoiceSettingsByCompany: {
      data: invoiceSettingsByCompany.data,
      isLoading: invoiceSettingsByCompany.isLoading,
    },
  };

  const { data: companiesData } = useCompaniesByLoggedEmployee();
  const companies = flatInfiniteData(companiesData);

  useEffect(() => {
    if (customer) {
      if (!customer?.showLgpd && !customer?.enableLgpd) {
        setIsAlert(true);
      } else {
        setIsAlert(false);
      }
    }
  }, [customer, workorderParams.dataUpdatedAt, workorderParams.isLoading]);

  const isPortocarWithClosedOrCancelled = useMemo(() => {
    return (
      isAppPortocar &&
      (complementData?.status === WorkOrderComplementStatus.Closed ||
        complementData?.status === WorkOrderComplementStatus.Cancelled)
    );
  }, [complementData]);

  function formatFormComplementData() {
    if (workorderData && complementData) {
      let formObj: WorkOrderComplementFormData = {
        ...complementData,
        origin: complementData?.origin
          ? ComplementOriginTranslator[complementData.origin]
          : undefined,
        companyId: workorderData.company.id,
        complementId: complementData.id,
        estimatorId: complementData.estimator?.id,
        entryDate: complementData.entryDate
          ? moment(complementData.entryDate)
          : undefined,
        authorizationDate: complementData.authorizationDate
          ? moment(complementData.authorizationDate)
          : undefined,
        closingDate: complementData.closingDate
          ? moment(complementData.closingDate)
          : undefined,
        estimatedLeaveDate: complementData.estimatedLeaveDate
          ? moment(complementData.estimatedLeaveDate)
          : undefined,
        returnDate: complementData.returnDate
          ? moment(complementData.returnDate)
          : undefined,
        lastOilChange: complementData.lastOilChange
          ? moment(complementData.lastOilChange)
          : undefined,
        scheduleDateTime: complementData.scheduleDateTime
          ? moment(complementData.scheduleDateTime)
          : undefined,
      };

      const company = companies?.find(
        (company) => company.id === workorderData.company.id,
      );
      if (company) {
        formObj = { ...formObj, companyId: company.id };
      }

      return formObj;
    }
  }

  return (
    <WorkoderDetailContext.Provider
      value={{
        workorder,
        complement,
        setComplementId,
        isAlerted,
        setIsAlert,
        company,
        formatFormComplementData,
        isPortocarWithClosedOrCancelled,
        items: {
          selectedItemsRows,
          setSelectedItemsRows,
        },
        complementCompanyId,
        setComplementCompanyId,
      }}
    >
      {!!workorder.data?.customer && (
        <LGPDTermModal
          customerId={workorder.data?.customer?.id}
          open={openLgpd}
          onClose={close}
          setAlert={setIsAlert}
        />
      )}
      <WorkOrderFormProvider>{children}</WorkOrderFormProvider>

      {!complement.data?.policyNumber && !complement.isLoading && (
        <WorkOrderVerifyPolicy
          complement={complement.data}
          workOrder={workorder.data}
          refetch={complementParams.refetch}
        />
      )}
    </WorkoderDetailContext.Provider>
  );
};

export default WorkorderDetailProvider;
